import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Space, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import Api from '../../Network';
import './style.scss';

interface CustomerTypeModalProps {
  visible: boolean;
  mode: 'create' | 'edit';
  customerType: any;
  onClose: () => void;
  onSuccess?: () => void;
}

const CustomerTypeModal: React.FC<CustomerTypeModalProps> = ({
  visible,
  mode,
  customerType,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Create customer type mutation
  const createCustomerTypeMutation = useMutation({
    mutationFn: (data: any) => Api.post('/api/admin/customer-types', data),
    onSuccess: () => {
      message.success(t('customerTypes.addSuccess', 'Customer type added successfully'));
      form.resetFields();
      onSuccess?.();
      onClose();
    },
    onError: (error: any) => {
      if (error?.response?.status === 422) {
        const errors = error?.response?.data?.errors || {};
        Object.keys(errors).forEach((key) => {
          form.setFields([
            {
              name: key,
              errors: Array.isArray(errors[key]) ? errors[key] : [errors[key]],
            },
          ]);
        });
      } else {
        const errorMessage =
          error?.response?.data?.message || t('common.error', 'Error adding customer type');
        message.error(errorMessage);
      }
    },
  });

  const updateCustomerTypeMutation = useMutation({
    mutationFn: (data: any) => Api.put(`/api/admin/customer-types/${customerType?.id}`, data),
    onSuccess: () => {
      message.success(t('customerTypes.updateSuccess', 'Customer type updated successfully'));
      form.resetFields();
      onSuccess?.();
      onClose();
    },
    onError: (error: any) => {
      if (error?.response?.status === 422) {
        const errors = error?.response?.data?.errors || {};
        Object.keys(errors).forEach((key) => {
          form.setFields([
            {
              name: key,
              errors: Array.isArray(errors[key]) ? errors[key] : [errors[key]],
            },
          ]);
        });
      } else {
        const errorMessage =
          error?.response?.data?.message || t('common.error', 'Error updating customer type');
        message.error(errorMessage);
      }
    },
  });

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      if (mode === 'create') {
        await createCustomerTypeMutation.mutateAsync(values);
      } else if (mode === 'edit') {
        await updateCustomerTypeMutation.mutateAsync(values);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  // Clear form errors when user starts typing
  const handleFieldChange = () => {
    form.setFields(
      form.getFieldsError().map((field) => ({
        name: field.name,
        errors: [],
      }))
    );
  };

  // Set form values when editing
  useEffect(() => {
    if (visible && customerType && mode === 'edit') {
      form.setFieldsValue({
        name_en: customerType.name_en,
        name_ar: customerType.name_ar,
      });
    } else if (visible && mode === 'create') {
      form.resetFields();
    }
  }, [visible, customerType, mode, form]);

  const getModalTitle = () => {
    switch (mode) {
      case 'create':
        return t('customerTypes.addNew', 'Add New Customer Type');
      case 'edit':
        return t('customerTypes.edit', 'Edit Customer Type');
      default:
        return '';
    }
  };

  return (
    <Modal
      title={getModalTitle()}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFieldsChange={handleFieldChange}
      >
        <Form.Item
          name="name_en"
          label={t('customerTypes.form.nameEn', 'Name (English)')}
          rules={[{ required: true, message: t('validation.required', 'This field is required') }]}
        >
          <Input placeholder={t('customerTypes.form.nameEnPlaceholder', 'Enter name in English')} />
        </Form.Item>

        <Form.Item
          name="name_ar"
          label={t('customerTypes.form.nameAr', 'Name (Arabic)')}
          rules={[{ required: true, message: t('validation.required', 'This field is required') }]}
        >
          <Input
            placeholder={t('customerTypes.form.nameArPlaceholder', 'Enter name in Arabic')}
            dir="rtl"
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>{t('common.cancel', 'Cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {mode === 'create' ? t('common.add', 'Add') : t('common.update', 'Update')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CustomerTypeModal;
